import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useRef, useState } from 'react';
import { ChatbotContext, ChatbotView } from '~/features';
import { FLOATING_BUTTON_BOTTOM, HIDE_WHILE_MODAL_OPEN } from './floatingButtons';

export const ChatbotButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const chatbotRef = ChatbotContext.useActorRef();
  const { error } = useSelector(chatbotRef, (state) => state.context);
  const panelRef = useRef<HTMLDivElement>(null);

  const openChat = () => {
    // Opening chatbot - ChatbotView will handle connection and greeting
    // Just toggle the UI state here
    setIsOpen(true);
  };

  const closeChat = () => {
    // Clear backend conversation context before closing
    const socket = chatbotRef.getSnapshot().context.socket;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'chatbot.reset' }));
    }

    chatbotRef.send({ type: 'ON_DISCONNECT' });
    // Reset conversation when closing the chat
    chatbotRef.send({ type: 'ON_RESET_CONVERSATION' });
    setIsOpen(false);
  };

  // MCV-1788: a click outside the open panel closes it exactly like the X (reset included).
  // The open button is not rendered while the panel is open, so no toggle guard is needed.
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      closeChat();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  return (
    // Above the signed-in footer (FLOATING_BUTTON_BOTTOM), and hidden under an open modal. The open panel's max height leaves the
    // same 1rem at the top: 100dvh minus the bottom offset minus 1rem at each breakpoint.
    <div className={`fixed ${FLOATING_BUTTON_BOTTOM} right-4 z-30 ${HIDE_WHILE_MODAL_OPEN}`}>
      {isOpen ? (
        <div
          ref={panelRef}
          className="bg-base text-content-primary rounded-lg shadow-lg w-160 max-w-[calc(100vw-2rem)] h-200 max-h-[calc(100dvh-8rem)] max-[340px]:max-h-[calc(100dvh-9.25rem)] sm:max-h-[calc(100dvh-7rem)] md:max-h-[calc(100dvh-5.5rem)] lg:max-h-[calc(100dvh-5rem)] flex flex-col bg-primary-t-600"
        >
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="font-semibold">Chat Support</h3>
            <button
              onClick={closeChat}
              aria-label="Close chat support"
              className="text-content-secondary hover:text-primary-500"
              data-cy={CT.chatbot.closeBtn}
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          {/* Use the ChatbotView component for the chat interface */}
          <ChatbotView />

          {error && (
            <div className="text-center py-2 border-t">
              <span className="text-danger text-sm">{error}</span>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={openChat}
          aria-label="Open chat support"
          className="bg-primary-500 text-white rounded-pill p-3 shadow-lg hover:bg-primary-600 hover:cursor-pointer"
          data-cy={CT.chatbot.openBtn}
        >
          <ChatBubbleLeftRightIcon className="size-6" />
        </button>
      )}
    </div>
  );
};
